import { useCallback, useEffect, useState } from 'react';
import { billingApi } from '../api/billing.api';
import type { LifecycleNudge, NudgeTriggerKey } from '../types/nudge.types';

/**
 * The active lifecycle nudge for this shop, or null.
 *
 * `null` is the expected steady state, not an error — most merchants have no
 * nudge on most page loads. `billingApi.getNudge` already swallows transport
 * failures into `null`, so this hook has no error channel to expose: there is
 * nothing a merchant could do about a failed upsell lookup, and rendering
 * anything about it would be worse than rendering nothing.
 */
export function useLifecycleNudge() {
  const [nudge, setNudge] = useState<LifecycleNudge | null>(null);
  const [loading, setLoading] = useState(true);
  /**
   * Trigger keys dismissed in this browser session. Held separately from
   * `nudge` so an optimistic dismiss survives a refetch that races the POST —
   * the server would otherwise hand back the same nudge and it would reappear
   * under the merchant's cursor.
   */
  const [dismissedKeys, setDismissedKeys] = useState<NudgeTriggerKey[]>([]);

  const fetchNudge = useCallback(async () => {
    setLoading(true);
    const data = await billingApi.getNudge();
    setNudge(data);
    setLoading(false);
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const data = await billingApi.getNudge();
      if (cancelled) return;
      setNudge(data);
      setLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  /**
   * Hide immediately, then persist. The merchant's click is the source of
   * truth for what is on screen; the POST only decides whether it stays gone
   * after a reload. A failed request is logged and otherwise ignored — putting
   * the banner back because a write failed is the one outcome guaranteed to
   * feel broken.
   */
  const dismiss = useCallback(async (triggerKey: NudgeTriggerKey) => {
    setDismissedKeys((keys) => (keys.includes(triggerKey) ? keys : [...keys, triggerKey]));
    try {
      await billingApi.dismissNudge(triggerKey);
    } catch (error) {
      console.error('Failed to dismiss lifecycle nudge:', error);
    }
  }, []);

  const visibleNudge =
    nudge && !dismissedKeys.includes(nudge.trigger_key) ? nudge : null;

  return { nudge: visibleNudge, loading, dismiss, refetch: fetchNudge };
}
